import { useMemo } from 'react';

import { RunSchema } from '@/types/schema';

import {
  EMPTY_LIST,
  FlatRun,
  Qualifier,
  RowDepthMeta,
  createCompactTree,
  qualifyFromHeuristics,
} from './utils';

export const useFoldedChildRuns = ({
  rootRuns,
  run,
  childRunLookup,
  filteredRuns,
  filteredOnly,
  userAllRuns,
  foldedRuns,
}: {
  rootRuns: RunSchema[];
  run?: RunSchema;
  childRunLookup: Record<string, RunSchema[] | undefined>;
  filteredRuns?: RunSchema[];
  filteredOnly: boolean;
  userAllRuns: boolean;
  foldedRuns: string[];
}): [Record<string, RunSchema[] | undefined> | undefined, number] => {
  return useMemo(() => {
    if (!childRunLookup) return [undefined, 0];

    const qualifyFromFilter = (run) =>
      !!filteredRuns?.find((r) => r.id === run.id);

    const qualifier: Qualifier[] = [qualifyFromHeuristics, qualifyFromFilter];

    const [compactChildRunLookup, selectedRuns, visitedSize] =
      createCompactTree({
        activeRun: run,
        rootRuns,
        childRunLookup,
        qualifier,
      });
    let hiddenCount = visitedSize - Object.keys(selectedRuns).length;

    const mChildRunLookup = userAllRuns
      ? { ...childRunLookup }
      : compactChildRunLookup;

    const rootRunIds = rootRuns.map((r) => r.id);
    // don't apply this filter logic to threads view because
    // we don't retrieve all nodes for all traces and this wouldn't work
    // correctly.
    if (filteredOnly && rootRuns.length <= 1) {
      const filteredRunPathSet = new Set(rootRunIds);
      for (const run of filteredRuns ?? []) {
        const selectedRun = selectedRuns[run.id];
        for (const parentId of selectedRun?.parent_run_ids ?? []) {
          filteredRunPathSet.add(parentId);
        }
        filteredRunPathSet.add(run.id);
      }

      const isInFilteredRunPath = (id: string) => filteredRunPathSet.has(id);
      const isFoldedManually = (id: string) => foldedRuns.includes(id);

      for (const id in mChildRunLookup) {
        // count the number of runs hidden by filters.
        const beforeFilterN = mChildRunLookup[id]?.length ?? 0;
        if (!isInFilteredRunPath(id)) {
          mChildRunLookup[id] = mChildRunLookup[id] ? EMPTY_LIST : undefined;
        } else if (isFoldedManually(id)) {
          mChildRunLookup[id] = EMPTY_LIST;
        } else {
          // fold the rest of children not in the filteredPath
          mChildRunLookup[id] = mChildRunLookup[id]?.filter((r) =>
            isInFilteredRunPath(r.id)
          );
        }
        hiddenCount += beforeFilterN - (mChildRunLookup[id]?.length ?? 0);
      }
    } else {
      for (const id of foldedRuns) {
        if (id in mChildRunLookup) {
          mChildRunLookup[id] = EMPTY_LIST;
        }
      }
    }

    return [mChildRunLookup, hiddenCount];
  }, [
    rootRuns,
    run,
    childRunLookup,
    filteredRuns,
    filteredOnly,
    userAllRuns,
    foldedRuns,
  ]);
};

const useTreeStructure = ({
  rootRuns,
  selectedRun,
  childRunLookupWithFolds,
}: {
  rootRuns: RunSchema[];
  selectedRun?: RunSchema;
  childRunLookupWithFolds?: Record<string, RunSchema[] | undefined>;
}) => {
  const selectedRunId = selectedRun?.id;
  return useMemo(() => {
    const runIdFlatPosIdx: Record<string, number> = {};
    const flattenedRuns: FlatRun[] = [];
    const depthMetaList: RowDepthMeta[] = [];

    const findIndex = (runId: string | undefined) => {
      if (!runId) return -1;
      return runIdFlatPosIdx[runId] ?? -1;
    };

    const rootRunIds = rootRuns.map((r) => r.id);
    // DFS pops from end of queue so reverse rootRuns to get the right order
    // but don't do it in place.
    const queue: FlatRun[] = [...rootRuns].reverse().map((rootRun, idx) => ({
      depth: 0,
      run: rootRun,
      isActiveDescendant: false,
      rootRunId: rootRun.id,
      conversationIdx: rootRuns.length - idx - 1,
    }));

    const visited = new Set<string>();
    while (queue.length > 0) {
      const item = queue.pop();

      if (!item) continue;
      const { depth, isActiveDescendant, run } = item;
      if (visited.has(run.id)) continue;

      visited.add(run.id);
      flattenedRuns.push(item);
      const newLength = depthMetaList.push({});
      runIdFlatPosIdx[run.id] = newLength - 1;

      queue.push(
        ...(
          childRunLookupWithFolds?.[run.id]?.map((run) => ({
            depth: depth + 1,
            run,
            isActiveDescendant:
              isActiveDescendant ||
              (!!selectedRunId &&
                run.id === selectedRunId &&
                !rootRunIds.includes(run.id)),
            rootRunId: item.rootRunId,
          })) ?? []
        ).reverse()
      );
    }

    for (let y = 0; y < flattenedRuns.length; ++y) {
      const run = flattenedRuns[y].run;
      const x = flattenedRuns[y].depth;

      // top-right arrows
      if (x > 0) {
        depthMetaList[y][x - 1] ??= {};
        depthMetaList[y][x - 1]['right'] =
          run.status === 'error'
            ? 'red'
            : run.status === 'interrupted'
            ? 'purple'
            : 'black';

        // draw red lines up from current node to parent
        // at x - 1 depth
        if (run.status === 'error' || run.status === 'interrupted') {
          const parentRunId =
            run.parent_run_id ?? run.parent_run_ids?.slice(-1)[0];
          const parentIdx = findIndex(parentRunId);
          if (parentIdx !== -1) {
            const selfIdx = y;
            // go from parent index to 1 above self
            for (let yi = parentIdx; yi < selfIdx; ++yi) {
              depthMetaList[yi][x - 1] ??= {};
              depthMetaList[yi][x - 1]['bottom'] =
                run.status === 'error' ? 'red' : 'purple';
            }
          }
        }
      }

      // draw lines down to children
      const lastChild = childRunLookupWithFolds?.[run.id]?.at(-1);
      const lastChildIdx = findIndex(lastChild?.id);
      const min = y;
      const max = lastChildIdx - 1;

      for (let yi = min; yi <= max; ++yi) {
        depthMetaList[yi][x] ??= {};
        depthMetaList[yi][x]['bottom'] = 'black';
      }
    }

    // blue lines
    let activeNodeIdx = selectedRunId ? findIndex(selectedRunId) : -1;
    while (activeNodeIdx >= 0) {
      const run = flattenedRuns[activeNodeIdx].run;
      const x = flattenedRuns[activeNodeIdx].depth;

      const parentRunId = run.parent_run_id ?? run.parent_run_ids?.slice(-1)[0];
      const parentNodeIdx = findIndex(parentRunId);

      if (x > 0 && parentNodeIdx !== -1) {
        depthMetaList[activeNodeIdx][x - 1] ??= {};
        depthMetaList[activeNodeIdx][x - 1]['right'] = 'blue';
        for (let yi = parentNodeIdx; yi < activeNodeIdx; yi += 1) {
          depthMetaList[yi][x - 1] ??= {};
          depthMetaList[yi][x - 1]['bottom'] = 'blue';
        }
      }

      activeNodeIdx = parentNodeIdx;
    }

    return { flattenedRuns, depthMetaList };
  }, [rootRuns, selectedRunId, childRunLookupWithFolds]);
};

export const useRunsInTreeStructure = ({
  rootRuns,
  run,
  childRunLookup = {},
  filteredRuns,
  filteredOnly,
  userAllRuns,
  foldedRuns,
}: {
  rootRuns: RunSchema[];
  run?: RunSchema;
  childRunLookup?: Record<string, RunSchema[] | undefined>;
  filteredRuns?: RunSchema[];
  filteredOnly: boolean;
  userAllRuns: boolean;
  foldedRuns: string[];
}) => {
  const [childRunLookupWithFolds, hiddenCount] = useFoldedChildRuns({
    rootRuns,
    run,
    childRunLookup,
    filteredRuns,
    filteredOnly,
    userAllRuns,
    foldedRuns,
  });

  const tree = useTreeStructure({
    rootRuns,
    selectedRun: run,
    childRunLookupWithFolds,
  });

  return {
    childRunLookupWithFolds,
    hiddenCount,
    tree,
  };
};

export const useFilteredRunsInTreeStructure = ({
  filteredRuns,
  rootRuns,
  selectedRun,
  foldedRuns,
}: {
  filteredRuns?: RunSchema[];
  rootRuns: RunSchema[];
  selectedRun?: RunSchema;
  foldedRuns: string[];
}) => {
  return useMemo(() => {
    // add all filtered runs
    const flattenedRuns: FlatRun[] = [];
    const depthMetaList: RowDepthMeta[] = [];

    const runs = filteredRuns?.slice(0) ?? [];
    runs.sort((a, b) => a.dotted_order.localeCompare(b.dotted_order));

    const childRunLookupWithFolds: Record<string, RunSchema[]> = {};
    let hiddenCount = 0;

    // return root run if it has a child in the filtered runs or if it's itself in the filtered runs
    // then put all the filtered children in depth root + 1
    for (const [idx, rootRun] of rootRuns.entries()) {
      const rootRunChildren = runs.filter((run) =>
        run.parent_run_ids?.includes(rootRun.id)
      );
      const isRootRunInFiltered = runs.some((run) => run.id === rootRun.id);

      // don't add root if it has no children and it's not in the filtered runs
      if (rootRunChildren.length === 0 && !isRootRunInFiltered) continue;

      flattenedRuns.push({
        run: rootRun,
        depth: 0,
        isActiveDescendant: false,
        rootRunId: rootRun.id,
        conversationIdx: idx,
      });
      depthMetaList.push({});

      const isRootRunFolded = foldedRuns.includes(rootRun.id);
      childRunLookupWithFolds[rootRun.id] = isRootRunFolded
        ? []
        : rootRunChildren;

      if (!isRootRunFolded) {
        for (const childRun of rootRunChildren) {
          flattenedRuns.push({
            run: childRun,
            depth: 1,
            isActiveDescendant: selectedRun?.id === rootRun.id,
            rootRunId: rootRun.id,
          });
          depthMetaList.push({});
        }
      } else {
        hiddenCount += rootRunChildren.length;
      }
    }

    return {
      tree: { flattenedRuns, depthMetaList },
      childRunLookupWithFolds,
      hiddenCount,
    };
  }, [filteredRuns, rootRuns, selectedRun, foldedRuns]);
};
